<template>
  <v-row class="mt-1">
    <v-col>
      <ValidationObserver ref="lotForm" v-slot="{ invalid, validated }">
        <v-expansion-panels multiple>
          <v-expansion-panel v-test="{ panel: '' }">
            <ValidationObserver ref="info" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="info">
                  <v-icon>
                    info
                  </v-icon>
                  {{ $t('Общая информация') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2 pt-0">
                    <validatable-text-input
                        v-model="lot.lotDescription"
                        :readonly="readonly"
                        rules="required"
                        :label="$t('Предмет договора')" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-date-picker
                        v-model="lot.publicationDate"
                        v-test="{ date: 'publicationDate' }"
                        rules="required"
                        dense
                        :label="$t('Дата публикации')" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-enum-combobox
                        v-model="lot.premiumType"
                        :values-enum="PREMIUM_TYPE_ENUM"
                        rules="required"
                        dense
                        :readonly="readonly"
                        :clearable="!readonly"
                        :label="$t('Способ расчета цены')" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2 pr-4 py-0">
                    <validatable-text-input
                        v-if="isFixPremium"
                        v-model="lot.currency"
                        :readonly="readonly"
                        rules="required"
                        :label="$t('Валюта контракта')" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-test="{ panel: '' }">
            <ValidationObserver ref="incoterms" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="incoterms">
                  <v-icon>
                    mdi-routes
                  </v-icon>
                  {{ $t('Условия и базис поставки Incoterms') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-incoterm-selection
                        v-model="lot.incotermId"
                        :label="$t('Условия поставки')"
                        rules="required"
                        :readonly="readonly || !isUserLoaded"
                        @change="changeIncoterm" />
                  </v-col>
                  <v-col
                      cols="12"
                      :md="$vuetify.breakpoint.lgAndDown ? 12 : 6">
                    <div v-if="hasBasises">
                      <general-error v-if="!!generalError" />
                      <v-progress-linear :active="isLoading" color="loader" indeterminate />
                      <validatable-delivery-basises
                          v-if="hasBasises"
                          v-model="lot.lotDeliveryBasises"
                          :incoterm-id="lot.incotermId"
                          :loading="isLoading" />
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-test="{ panel: '' }">
            <ValidationObserver ref="contacts" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="contact_mail">
                  <v-icon>
                    contact_mail
                  </v-icon>
                  {{ $t('Контактная информация') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-text-input
                        v-model="lot.contactPerson"
                        v-test="{ input: 'contactPerson' }"
                        :readonly="readonly || !isUserLoaded"
                        prepend-icon="person"
                        rules="required"
                        dense
                        :label="$t('Контактное лицо')" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-text-input
                        v-model="lot.contactPhone"
                        v-test="{ input: 'contactPhone' }"
                        dense
                        :readonly="readonly || !isUserLoaded"
                        :rules="{ required: true,
                                  regex: /^[+]?[\d\s(\-);,]+$/ }"
                        :label="$t('Контактный телефон')"
                        prepend-icon="settings_phone"
                        :type="'tel'" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2">
                    <validatable-text-input
                        v-model="lot.contactEmail"
                        v-test="{ input: 'contactEmail' }"
                        dense
                        :readonly="readonly || !isUserLoaded"
                        rules="required|email"
                        :label="$t('Электронный адрес')"
                        prepend-icon="alternate_email" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

        </v-expansion-panels>
        <v-alert
            class="text-center"
            :value="invalid && validated"
            type="error">
          {{ $t('Не все поля заполнены.') }}
        </v-alert>
      </ValidationObserver>
    </v-col>
  </v-row>
</template>

<script>
  import Vue from 'vue'
  import lodash from 'lodash'
  import ValidatableDatePicker from '@/components/reused/ui/ValidatableDatePicker.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableIncotermSelection from '@/components/reused/ui/ValidatableIncotermSelection.vue'
  import ValidatableDeliveryBasises from '@/components/reused/ui/ValidatableDeliveryBasises.vue'
  import {
    LOT_TYPE_ENUM,
    LOT_STATUS_TYPE_ENUM,
    PREMIUM_TYPE_ENUM
  } from '@/constants/enums'
  import { LOT_STATUSES, DICTIONARY_TYPES, PREMIUM_TYPES } from '@/constants/business'
  import { mapState, mapActions } from 'vuex'
  import { CURRENT_USER, DICTIONARIES } from '@/constants/store.js'
  import GeneralError from '@/components/reused/common/GeneralError.vue'

  export default {
    components: {
      ValidatableDatePicker,
      ValidatableTextInput,
      ValidatableEnumCombobox,
      ValidatableIncotermSelection,
      ValidatableDeliveryBasises,
      GeneralError
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      },
      saveMethod: {
        type: Function
      },
      lotInfo: {
        type: Object,
        required: true
      }
    },
    data () {
      return {
        LOT_TYPE_ENUM,
        DICTIONARY_TYPES,
        LOT_STATUS_TYPE_ENUM,
        PREMIUM_TYPE_ENUM,
        PREMIUM_TYPES,
        lodash,
        lot: {
          creatorId: null,
          lotDescription: null,
          incotermId: null,
          publicationDate: null,
          premiumType: null,
          currency: null,
          contactPerson: null,
          contactPhone: null,
          contactEmail: null,
          lotDeliveryBasises: []
        },
        lastTimerId: null,
        itemsPerPage: 5
      }
    },
    computed: {
      ...mapState(CURRENT_USER.NAMESPACE, {
        profile: state => (state || {})
      }),
      ...mapState(DICTIONARIES.NAMESPACE, {
        incoterms: state => state[DICTIONARY_TYPES.INCOTERMS],
        deliveryBasises: state => {
          return state[DICTIONARY_TYPES.DELIVERY_BASISES] || []
        }
      }),
      hasChanges () {
        return Object.keys(this.lot).some(key => {
          if (key === 'creatorId') {
            return this.isAdmin
              ? this.lot.creatorId !== this.lotInfo.creatorId
              : false
          } else if (key === 'lotDeliveryBasises') {
            return !lodash.isEqual(this.lot.lotDeliveryBasises, this.lotInfo.lotDeliveryBasises)
          }
          return this.lot[key] !== this.lotInfo[key]
        })
      },
      isFixPremium () {
        return this.lot.premiumType === PREMIUM_TYPES.FIX
      },
      isUserLoaded () {
        return !!this.profile.contactPerson
      },
      canEditField () {
        return !this.readonly &&
          (!this.lotInfo.lotStatus || this.lotInfo.lotStatus === LOT_STATUSES.DRAFT)
      },
      hasBasises () {
        if (this.lot.incotermId) {
          return this.incoterms.some(i => i.incotermId === this.lot.incotermId && i.hasBasises)
        }
        return false
      },
      deliveryBasisHeaders () {
        return [
          { text: this.$t('Маршрут поставки'), align: 'start', sortable: false, value: 'description', width: '70%' },
          { text: this.$t('Расходы продавца по доставке покупателю'), align: 'start', sortable: false, value: 'shippingCost', width: '20%' },
          { text: this.$t('Маршрут разрешен для торговой процедуры'), align: 'start', sortable: false, value: 'selected' }
        ]
      }
    },
    watch: {
      lotInfo: {
        deep: true,
        immediate: true,
        handler: function (newVal) {
          this.mapFields(newVal)
        }
      },
      profile: {
        deep: true,
        immediate: true,
        handler: function (newVal) {
          if (!this.isAdmin &&
            !this.readonly &&
            newVal.contactPerson &&
            !this.lotInfo.lotId) {
            this.setContactPersonInfo(newVal)
          }
        }
      }
    },
    beforeDestroy: async function () {
      await this.clearStore()
      if (this.lastTimerId) {
        clearTimeout(this.lastTimerId)
        this.lastTimerId = null
      }
    },
    created: async function () {
      await this.clearStore()
    },
    methods: {
      ...mapActions(DICTIONARIES.NAMESPACE, {
        getBusinessDictionaryItemsAction: DICTIONARIES.ACTIONS.GET_BUSINESS_DICTIONARY_ITEMS,
        clearDictionaryItemsAction: DICTIONARIES.ACTIONS.CLEAR_DICTIONARY_ITEMS
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearDictionaryItemsAction({ dictionaryType: DICTIONARY_TYPES.DELIVERY_BASISES })
          ])
      },
      validateAndSave: async function () {
        const result = await this.$refs.lotForm.validate()
        if (!result) {
          throw new Vue.errorsTypes.ValidationError()
        }
        // если изменений нет, то сохранение не нужно
        if (this.readonly || !this.hasChanges) {
          return
        }
        if (this.saveMethod) {
          await this.saveMethod(this.lot)
        }
      },
      setContactPersonInfo: async function (info) {
        const vm = this
        let timeout = 1
        if (!info.contactPerson) {
          timeout = 1500
        }

        if (vm.lastTimerId) {
          clearTimeout(vm.lastTimerId)
          vm.lastTimerId = null
        }

        vm.lastTimerId = setTimeout((info1) => {
          if (!info1.contactPerson) { return }
          vm.lot.contactPerson = info1.contactPerson || null
          vm.lot.contactPhone = info1.phone || info1.contactPhone || null
          vm.lot.contactEmail = info1.email || info1.contactEmail || null
        }, timeout, info)
      },
      mapFields: async function (lot) {
        try {
          this.isLoading = true

          const profile = lot.lotId
            ? lot
            : this.profile

          await this.setContactPersonInfo(profile)
          if (lot.publicationDate) {
            this.lot.publicationDate = lot.publicationDate
          }
          if (!lot.lotId) {
            return
          }

          if (this.lot.incotermId !== lot.incotermId) {
            await this.getBusinessDictionaryItemsAction({ dictionaryType: DICTIONARY_TYPES.DELIVERY_BASISES, incotermId: lot.incotermId })
          }

          for (const key in this.lot) {
            this.lot[key] = lodash.cloneDeep(lot[key])
          }

          if (lot.premiumType === PREMIUM_TYPES.DIFFERENTIAL) {
            this.lot.currency = null
          }
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      changeIncoterm: async function (item) {
        await this.getBusinessDictionaryItemsAction({ dictionaryType: DICTIONARY_TYPES.DELIVERY_BASISES, incotermId: item.incotermId })
        this.lot.lotDeliveryBasises = lodash.cloneDeep(this.deliveryBasises)
      }
    }
  }
</script>
